import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Input, Label, Spinner } from "reactstrap";
import { getJobs } from "../../../../api/jobs";
import ApplicationItem from "./ApplicationItem";

const AllApplications = ({ jobApplications }) => {
  const { profile } = useSelector((state) => state.customer);

  const [selectedJobId, setSelectedJobId] = useState("");
  const [allPostedJobs, setAllPostedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  // 🎯 fetch EVERY job this employer has posted
  useEffect(() => {
    if (!profile?.id) return;

    setJobsLoading(true);
    getJobs(`&JobsSearch[employer_id]=${profile.id}`)
      .then((res) => {
        setAllPostedJobs(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching posted jobs:", err);
        setAllPostedJobs([]);
      })
      .finally(() => setJobsLoading(false));
  }, [profile?.id]);

  // 🎯 Deduplicate jobs by title for the dropdown selector
  const uniquePostedJobs = useMemo(() => {
    const seenTitles = new Set();
    return allPostedJobs.filter((job) => {
      const title = (job?.job_title || job?.jobTitle || "").trim().toLowerCase();
      if (!title || seenTitles.has(title)) {
        return false;
      }
      seenTitles.add(title);
      return true;
    });
  }, [allPostedJobs]);

  const filteredApplications = useMemo(() => {
    if (!selectedJobId) return jobApplications;

    return jobApplications?.filter(
      (data) => String(data?.job?.id) === String(selectedJobId),
    );
  }, [jobApplications, selectedJobId]);

  const selectedJobTitle = useMemo(() => {
    if (!selectedJobId) return "";
    const job = allPostedJobs.find(
      (j) => String(j?.id) === String(selectedJobId),
    );
    return job?.job_title || job?.jobTitle || "";
  }, [allPostedJobs, selectedJobId]);

  return (
    <>
      <Row className="mb-4">
        <Col md={4}>
          <Label htmlFor="jobFilter" className="form-label">
            Filter by Posted Job
          </Label>
          <Input
            type="select"
            id="jobFilter"
            className="form-select"
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            disabled={jobsLoading}
          >
            <option value="">All Jobs</option>
            {uniquePostedJobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.job_title || job.jobTitle}
              </option>
            ))}
          </Input>
          {jobsLoading && (
            <div className="mt-2">
              <Spinner size="sm" /> <span className="text-muted small">Loading jobs...</span>
            </div>
          )}
        </Col>
      </Row>

      {filteredApplications?.length > 0 ? (
        filteredApplications.map((data) => (
          <ApplicationItem key={data?.id} data={data} />
        ))
      ) : (
        <p className="text-muted text-center mt-4">
          {selectedJobId
            ? `No applications found for "${selectedJobTitle}".`
            : "No applications found."}
        </p>
      )}
    </>
  );
};

export default AllApplications;